import { Outlet, useOutletContext } from 'react-router-dom'

import { Box, Prose, Text } from '@factorialco/deprecated-design-system'
import { AtsJobApplicationOverviewFragment } from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import i18n from 'modules/core/lib/i18n'

import { CandidateApplicationsList } from 'modules/ats/components/CandidateApplicationsList'
import { InterviewListing } from 'modules/ats/components/InterviewListing'

import { AISummary } from './AISummary'
import { AtsAnswerCard } from './AtsAnswerCard'

interface OutletContext {
  application: AtsJobApplicationOverviewFragment
}

const JobApplicationOverview = () => {
  const { application } = useOutletContext<OutletContext>()
  const noData = !application.coverLetter && !application.answers?.length

  return (
    <>
      <Box gap="s24">
        <InterviewListing applicationId={application.id} />

        {!!application.summary && <AISummary data={application.summary} />}

        {noData && (
          <Box padding="s40" gap="s16" width="full" alignItems="center" textAlign="center">
            <Text.Normal>{i18n.t('ats.candidates.overview.candidate_profile.no_data')}</Text.Normal>
            <HelpCenterLink name="ats_create_offer">
              <Text.Normal weight="medium">
                {i18n.t('ats.candidates.overview.candidate_profile.help_link')}
              </Text.Normal>
            </HelpCenterLink>
          </Box>
        )}

        {application.coverLetter && (
          <Box gap="s12">
            <Text.Normal size="200" color="textSecondary" weight="medium">
              {i18n.t('ats.application_details.cover_letter_title')}
            </Text.Normal>
            <Box
              padding="s16"
              borderRadius={{ all: 'abs016' }}
              width="full"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              <Prose color="textPrimary" size="200" fullWidth>
                {application.coverLetter.replaceAll('\n', '<br />')}
              </Prose>
            </Box>
          </Box>
        )}

        {!!application.answers.length && (
          <Box gap="s12">
            <Text.Normal size="200" color="textSecondary" weight="medium">
              {i18n.t('ats.application_details.custom_questions_title')}
            </Text.Normal>
            <Box
              borderRadius={{ all: 'abs016' }}
              width="full"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              {application.answers.map((answer, index) => (
                <AtsAnswerCard
                  key={answer.id}
                  answer={answer}
                  isLastItem={application.answers.length - 1 === index}
                />
              ))}
            </Box>
          </Box>
        )}

        <CandidateApplicationsList
          totalApplicationsCount={application.candidate.atsJobPostingIds?.length || 0}
          currentApplicationId={application.id}
          relatedApplications={application.candidate.applicationsConnection.nodes || []}
          title={i18n.t('ats.application_details.other_applications')}
        />
      </Box>
      <Outlet context={{ application }} />
    </>
  )
}

export default JobApplicationOverview
